import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel,
  Grid, IconButton, InputLabel, LinearProgress, MenuItem, Paper, Radio, RadioGroup,
  Select, Snackbar, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@/client/services/user";
import { ExecutionResultPanel, LANGUAGES_LIST, PALETTE, SectionPaper } from "../components/shared";
import { saveAnswer, startTestAttempt, submitTest } from "../hooks/useSkillTests";
import { getStudentOrg } from "../utils/enrollment";
import { getDefaultProgrammingLanguage, resolveProgrammingLanguages } from "../utils/programmingLanguages";
import { findLanguageByAlias, getBoilerplateKey, getLanguageById } from "../utils/judge0Languages";
import SkillTestResultView from "./SkillTestResultView";
import axios from "axios";

const API_CP = "/api/integration/external-service/coding-platform";
const API_SKILL_TESTS = "/api/integration/external-service/coding-platform/skill-tests";

const ANSWERED_BG = "#86d293";
const ANSWERED_TEXT = "#14532d";
const ANSWERED_BORDER = "#22c55e";

type Props = { testId: string; onBack: () => void };
type Phase = "loading" | "taking" | "submitting" | "result";

export default function SkillTestPlayerView({ testId, onBack }: Props) {
  const { user } = useUser();
  const studentId = user?.id ?? "";

  const [phase, setPhase] = useState<Phase>("loading");
  const [attempt, setAttempt] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [testMeta, setTestMeta] = useState<any>(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, { answer?: string; code?: string; programmingLanguageId?: number; programmingLanguage?: string; customInput?: string }>>({});
  const [result, setResult] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [codeExecution, setCodeExecution] = useState<Record<string, any>>({});
  const [running, setRunning] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const timerRef = useRef<any>(null);
  const fiveMinWarnedRef = useRef(false);
  const submitConfirmedRef = useRef<() => void>(() => {});

  const isQuestionAnswered = useCallback((qs: any) => {
    const qsid = qs._id ?? qs.id;
    const a = answers[qsid];
    return !!(a?.answer?.trim?.() || a?.code?.trim?.());
  }, [answers]);

  const answeredCount = useMemo(
    () => questions.filter(isQuestionAnswered).length,
    [questions, isQuestionAnswered],
  );
  const unansweredCount = Math.max(questions.length - answeredCount, 0);

  // ── Start ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!studentId) return;
    const org = getStudentOrg(user);
    startTestAttempt(studentId, testId, {
      name: user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() || user?.email || "",
      email: user?.email ?? "",
      organizationName: org.organizationName,
      organizationId: org.organizationId,
    }).then((res) => {
      const a = res?.attempt ?? res;
      const qs = res?.questions ?? [];
      const meta = res?.testMeta ?? res?.test ?? null;
      setAttempt(a); setQuestions(qs);
      setTestMeta(meta);
      // durationMinutes lives on the test, fall back to 30 min
      const duration = meta?.durationMinutes ?? a?.durationMinutes ?? 30;
      const totalSeconds = duration * 60;

      // If resumed, calculate real remaining time from startedAt
      if (res?.resumed && a?.startedAt) {
        const elapsed = Math.floor((Date.now() - new Date(a.startedAt).getTime()) / 1000);
        const remaining = Math.max(totalSeconds - elapsed, 0);
        setTimeLeft(remaining);
        // If time already expired, auto-submit immediately
        if (remaining <= 0) {
          setPhase("taking"); // briefly so submit handler fires
          setTimeout(() => submitConfirmedRef.current(), 0);
          return;
        }
      } else {
        setTimeLeft(totalSeconds);
      }

      setPhase("taking");
      if (res?.resumed && a?.answers?.length) {
        const map: Record<string, any> = {};
        (a.answers ?? []).forEach((ans: any) => { map[ans.questionId] = ans; });
        setAnswers(map);
      }
    }).catch(() => setPhase("result"));
  }, [testId, studentId]);

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmitConfirmed = useCallback(async () => {
    clearInterval(timerRef.current);
    setShowSubmitDialog(false);
    setPhase("submitting");
    try {
      const startedAt = attempt?.startedAt ? new Date(attempt.startedAt).getTime() : Date.now();
      const timeTaken = Math.round((Date.now() - startedAt) / 1000);
      const res = await submitTest(studentId, attempt._id, timeTaken);
      setResult(res);
    } catch { setResult(null); }
    setPhase("result");
  }, [attempt, studentId]);

  submitConfirmedRef.current = handleSubmitConfirmed;

  const requestSubmit = () => setShowSubmitDialog(true);

  // ── Timer ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "taking") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t === 300 && !fiveMinWarnedRef.current) {
          fiveMinWarnedRef.current = true;
          setSnackbarOpen(true);
        }
        if (t <= 1) {
          clearInterval(timerRef.current);
          submitConfirmedRef.current();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // ── Warn on refresh/close while test is in progress ───────────────────────
  useEffect(() => {
    if (phase !== "taking") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Your answers may not be saved. Are you sure you want to leave?";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase]);

  const fmtTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const fmtRemaining = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins} min ${secs} sec`;
  };
  const timerColor = timeLeft < 120 ? "#dc2626" : timeLeft < 300 ? "#f59e0b" : "#16a34a";

  const testLanguage = testMeta?.programmingLanguage;
  const allowedLanguages = useMemo(() => resolveProgrammingLanguages(testLanguage), [testLanguage]);

  const getDefaultCode = (q: any, langValue?: string) => {
    const lang = langValue ?? testLanguage ?? q.programmingLanguage ?? "python";
    const fromProblem = q.boilerplateCode?.[lang];
    if (fromProblem) return fromProblem;
    return findLanguageByAlias(lang)?.boilerplate ?? getDefaultProgrammingLanguage(testLanguage).boilerplate;
  };

  const getDefaultLanguage = (q: any) => {
    if (testLanguage) return getDefaultProgrammingLanguage(testLanguage);
    const langValue = q.programmingLanguage
      ?? (q.boilerplateCode ? Object.keys(q.boilerplateCode)[0] : "python");
    return findLanguageByAlias(langValue) ?? getDefaultProgrammingLanguage(testLanguage);
  };

  // ── Answer ───────────────────────────────────────────────────────────────────
  const setAnswer = async (qid: string, patch: any) => {
    const updated = { ...(answers[qid] ?? {}), ...patch };
    setAnswers((p) => ({ ...p, [qid]: updated }));
    if (attempt) saveAnswer({ studentId, attemptId: attempt._id, questionId: qid, ...updated }).catch(() => {});
  };

  const resolveAnswerLanguage = (ans: any, q: any) => {
    if (ans.programmingLanguageId) return getLanguageById(ans.programmingLanguageId) ?? getDefaultLanguage(q);
    if (ans.programmingLanguage) return findLanguageByAlias(ans.programmingLanguage) ?? getDefaultLanguage(q);
    return getDefaultLanguage(q);
  };

  // ── Run code ─────────────────────────────────────────────────────────────────
  const runCode = async (q: any) => {
    const qid = q._id ?? q.id;
    const ans = answers[qid] ?? {};
    const lang = resolveAnswerLanguage(ans, q);
    const code = ans.code ?? getDefaultCode(q, getBoilerplateKey(lang));
    if (!code?.trim()) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API_SKILL_TESTS}/run`, {
        studentId,
        questionId: qid,
        code,
        programmingLanguage: lang.key ?? lang.name,
        programmingLanguageId: lang.id,
        customInput: ans.customInput,
      });
      const data = res?.data?.result ?? res?.data;
      setCodeExecution((p) => ({ ...p, [qid]: data }));
    } finally { setRunning(false); }
  };

  // ── Result view ───────────────────────────────────────────────────────────────
  if (phase === "result") {
    if (!result) return <Alert severity="error" sx={{ m: 2 }}>Failed to load results. <Button onClick={onBack}>Back</Button></Alert>;
    return <SkillTestResultView result={result} questions={questions} answers={answers} testMeta={testMeta} onBack={onBack} />;
  }

  if (phase === "loading" || phase === "submitting") {
    return <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
      <CircularProgress /><Typography sx={{ color: PALETTE.textSecondary }}>{phase === "submitting" ? "Submitting..." : "Loading test..."}</Typography>
    </Box>;
  }

  const q = questions[current];
  const qid = q?._id ?? q?.id;
  const ans = answers[qid] ?? {};

  return (
    <Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="warning" sx={{ width: "100%", fontWeight: 600 }}>
          5 minutes left
        </Alert>
      </Snackbar>

      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberOutlinedIcon sx={{ color: "#dc2626" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Finish Test</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
              Remaining Time: <Box component="span" sx={{ fontWeight: 700, color: timerColor }}>{fmtRemaining(timeLeft)}</Box>
            </Typography>
            <IconButton size="small" onClick={() => setShowSubmitDialog(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>Your Test Summary</Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <SummaryRow label="Total Questions" value={questions.length} dotColor="#94a3b8" />
            <SummaryRow label="Answered" value={answeredCount} dotColor={ANSWERED_BORDER} />
            <SummaryRow label="Not Answered" value={unansweredCount} dotColor="#cbd5e1" />
          </Stack>
          <Typography sx={{ fontSize: 13, color: PALETTE.textSecondary, lineHeight: 1.6 }}>
            {unansweredCount > 0
              ? `You have not answered ${unansweredCount} question${unansweredCount > 1 ? "s" : ""}. Are you sure you want to submit the test?`
              : "Are you sure you want to submit the test?"}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setShowSubmitDialog(false)} variant="outlined" sx={{ textTransform: "none" }}>
            No, Back to Test
          </Button>
          <Button onClick={handleSubmitConfirmed} variant="contained" color="error" sx={{ textTransform: "none" }}>
            Finish Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Header ── */}
      <Paper sx={{ p: 1.25, mb: 1.5, borderRadius: "12px", border: `1px solid ${PALETTE.border}`, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: "none", fontSize: "12px" }}>Exit</Button>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>{testMeta?.title ?? "Skill Test"}</Typography>
          <Typography sx={{ fontSize: "11px", color: PALETTE.textSecondary }}>{answeredCount}/{questions.length} answered</Typography>
        </Box>
        <LinearProgress variant="determinate" value={questions.length ? (answeredCount / questions.length) * 100 : 0} sx={{ width: 100, height: 6, borderRadius: 3 }} />
        <Box sx={{ px: 1.5, py: 0.5, borderRadius: "8px", border: `2px solid ${timerColor}`, minWidth: 72, textAlign: "center" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 800, color: timerColor, fontFamily: "monospace" }}>{fmtTime(timeLeft)}</Typography>
        </Box>
        <Button variant="contained" color="error" size="small" onClick={requestSubmit} sx={{ textTransform: "none", fontSize: "12px" }}>Submit Test</Button>
      </Paper>

      <Grid container spacing={1.5}>
        {/* ── Question navigator ── */}
        <Grid item xs={12} lg={2.5}>
          <SectionPaper sx={{ p: 1.25 }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 1 }}>Questions</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "6px" }}>
              {questions.map((qs: any, i: number) => {
                const isAnswered = isQuestionAnswered(qs);
                return (
                  <Tooltip key={i} title={`Q${i + 1}`}>
                    <Box onClick={() => setCurrent(i)} sx={{ width: 28, height: 28, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 700, cursor: "pointer",
                      bgcolor: i === current ? "primary.main" : isAnswered ? ANSWERED_BG : PALETTE.bgLight,
                      color: i === current ? "#fff" : isAnswered ? ANSWERED_TEXT : "#374151",
                      border: `1px solid ${i === current ? "transparent" : isAnswered ? ANSWERED_BORDER : PALETTE.borderDark}`,
                    }}>{i + 1}</Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Divider sx={{ my: 1.25 }} />
            <Stack spacing={0.5}>
              {[["Not visited", PALETTE.bgLight, PALETTE.borderDark], ["Answered", ANSWERED_BG, ANSWERED_BORDER], ["Current", "primary.main", "transparent"]].map(([label, bg, border]) => (
                <Stack key={label as string} direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ width: 14, height: 14, borderRadius: "3px", bgcolor: bg, border: `1px solid ${border}` }} />
                  <Typography sx={{ fontSize: "10px", color: PALETTE.textSecondary }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
          </SectionPaper>
        </Grid>

        {/* ── Question area ── */}
        <Grid item xs={12} lg={9.5}>
          {q && (
            <SectionPaper>
              <Box sx={{ p: 1.5, borderBottom: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bgLight }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>Question {current + 1} of {questions.length}</Typography>
                  <Chip size="small" label={q.type?.replace(/_/g, " ")} sx={{ fontSize: "10px", height: "18px", textTransform: "capitalize" }} />
                  <Chip size="small" label={`${q.marks ?? 1} mark${(q.marks ?? 1) > 1 ? "s" : ""}`} sx={{ fontSize: "10px", height: "18px", bgcolor: "#eff6ff", color: "primary.main" }} />
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 2, lineHeight: 1.6 }}>{q.questionText}</Typography>

                {q.codeSnippet && (
                  <Box component="pre" sx={{ bgcolor: "#1e293b", color: "#e2e8f0", p: 1.5, borderRadius: "8px", fontSize: "12px", mb: 2, overflowX: "auto", fontFamily: "monospace" }}>
                    {q.codeSnippet}
                  </Box>
                )}

                {/* MCQ / output / debugging */}
                {(q.type === "mcq" || q.type === "output" || q.type === "debugging") && (
                  <RadioGroup value={ans.answer ?? ""} onChange={(e) => setAnswer(qid, { answer: e.target.value })}>
                    {(q.options ?? []).map((opt: string, oi: number) => (
                      <FormControlLabel key={oi} value={opt} control={<Radio size="small" />}
                        label={<Typography sx={{ fontSize: "13px" }}>{opt}</Typography>} />
                    ))}
                  </RadioGroup>
                )}

                {/* Short answer */}
                {q.type === "short_answer" && (
                  <TextField multiline minRows={3} fullWidth placeholder="Your answer..." value={ans.answer ?? ""}
                    onChange={(e) => setAnswer(qid, { answer: e.target.value })}
                    sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }} />
                )}

                {/* Coding question */}
                {q.type === "coding" && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <FormControl size="small" sx={{ width: 140 }}>
                        {allowedLanguages.length === 1 ? (
                          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: PALETTE.textSecondary, pt: 0.5 }}>
                            {allowedLanguages[0].name}
                          </Typography>
                        ) : (
                          <>
                            <InputLabel sx={{ fontSize: "12px" }}>Language</InputLabel>
                            <Select
                              label="Language"
                              value={resolveAnswerLanguage(ans, q).value}
                              sx={{ fontSize: "12px" }}
                              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
                              onChange={(e) => {
                                const l = allowedLanguages.find((x) => x.value === e.target.value) ?? allowedLanguages[0];
                                setAnswer(qid, {
                                  programmingLanguage: l.key ?? l.name,
                                  programmingLanguageId: l.id,
                                  code: ans.code ?? getDefaultCode(q, getBoilerplateKey(l)),
                                });
                              }}
                            >
                              {allowedLanguages.map((l) => (
                                <MenuItem key={l.value} value={l.value} sx={{ fontSize: "12px" }}>{l.name}</MenuItem>
                              ))}
                            </Select>
                          </>
                        )}
                      </FormControl>
                      <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} disabled={running} onClick={() => runCode(q)} sx={{ textTransform: "none", fontSize: "12px" }}>Run</Button>
                    </Stack>
                    <TextField multiline minRows={12} fullWidth
                      value={ans.code ?? getDefaultCode(q, getBoilerplateKey(resolveAnswerLanguage(ans, q)))}
                      onChange={(e) => setAnswer(qid, { code: e.target.value })}
                      sx={{ "& textarea": { fontFamily: "monospace", fontSize: "12px" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }} />
                    {running && <LinearProgress sx={{ mt: 0.75, borderRadius: 1 }} />}
                    {codeExecution[qid] && <Box sx={{ mt: 1 }}><ExecutionResultPanel execution={codeExecution[qid]} /></Box>}
                  </Box>
                )}
              </Box>

              {/* Nav buttons */}
              <Box sx={{ px: 2, pb: 1.5, display: "flex", justifyContent: "space-between" }}>
                <Button size="small" variant="outlined" disabled={current === 0} onClick={() => setCurrent((p) => p - 1)} sx={{ textTransform: "none", fontSize: "12px" }}>← Previous</Button>
                {current < questions.length - 1 ? (
                  <Button size="small" variant="contained" onClick={() => setCurrent((p) => p + 1)} sx={{ textTransform: "none", fontSize: "12px" }}>Next →</Button>
                ) : (
                  <Button size="small" variant="contained" color="success" startIcon={<SendIcon />} onClick={requestSubmit} sx={{ textTransform: "none", fontSize: "12px" }}>Submit Test</Button>
                )}
              </Box>
            </SectionPaper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function SummaryRow({ label, value, dotColor }: { label: string; value: number; dotColor: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 13, color: "#374151", flex: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{value}</Typography>
    </Stack>
  );
}
